/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState } from 'react';
import { useWBL } from '../../context/WBLContext';
import { Eye, LogOut, ShieldAlert, ChevronDown, ChevronUp } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';

export const ProxyFloatingIndicator: React.FC = () => {
  const { activeUser, isProxyActive, proxyOriginalSuperadmin, exitProxySession } = useWBL();
  const [isExpanded, setIsExpanded] = useState(false);

  if (!isProxyActive || !activeUser) return null;

  return (
    <div className="fixed bottom-5 right-5 z-[9999] font-sans text-xs select-none pointer-events-auto">
      <AnimatePresence>
        {isExpanded ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 10 }}
            transition={{ duration: 0.18 }}
            className="w-80 rounded-2xl bg-slate-900/95 backdrop-blur-md text-white border border-amber-500/40 shadow-2xl p-4 flex flex-col gap-3"
          >
            {/* Header */}
            <div className="flex items-center justify-between border-b border-slate-800 pb-2.5">
              <div className="flex items-center gap-2">
                <span className="flex h-2.5 w-2.5 relative">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-amber-500"></span>
                </span>
                <span className="font-bold text-[11px] uppercase tracking-wider text-amber-400 flex items-center gap-1.5">
                  <Eye className="w-3.5 h-3.5 text-amber-400" />
                  Superadmin Proxy Active
                </span>
              </div>
              <button
                type="button"
                onClick={() => setIsExpanded(false)}
                className="text-slate-400 hover:text-white p-1 rounded-lg hover:bg-slate-800 transition-colors"
                title="Minimize indicator"
              >
                <ChevronDown className="w-4 h-4" />
              </button>
            </div>

            {/* Impersonated User Profile Info */}
            <div className="bg-slate-800/80 rounded-xl p-3 border border-slate-700/60 flex flex-col gap-1">
              <div className="flex items-center justify-between">
                <span className="text-[10px] uppercase font-bold text-slate-400 tracking-wider">Mimicking User</span>
                <span className="px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-300 border border-amber-500/30 text-[9px] font-extrabold uppercase">
                  {activeUser.role}
                </span>
              </div>
              <p className="font-bold text-sm text-white truncate">{activeUser.name || 'Unnamed User'}</p>
              <p className="text-[11px] text-slate-300 truncate">{activeUser.email}</p>
              {(activeUser.school || activeUser.orgName) && (
                <p className="text-[10px] text-slate-400 truncate mt-0.5">
                  {activeUser.school || activeUser.orgName}
                </p>
              )}
            </div>

            {/* Safeguard Note */}
            <div className="flex items-start gap-2 text-[10px] text-amber-200/90 bg-amber-950/40 border border-amber-800/40 p-2.5 rounded-xl">
              <ShieldAlert className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
              <span>
                <strong>View-Only Safeguard:</strong> Modifying actions are protected to prevent unintended alterations to live records.
              </span>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center gap-2 pt-1">
              <button
                type="button"
                onClick={exitProxySession}
                className="flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-xl bg-amber-500 hover:bg-amber-400 text-slate-950 font-black text-[11px] uppercase tracking-wider transition-all shadow-md hover:shadow-amber-500/25 cursor-pointer"
              >
                <LogOut className="w-3.5 h-3.5" />
                Exit Proxy View
              </button>
              <button
                type="button"
                onClick={() => setIsExpanded(false)}
                className="px-3 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 font-semibold text-[11px] transition-colors cursor-pointer"
              >
                Hide
              </button>
            </div>
          </motion.div>
        ) : (
          <motion.button
            key="collapsed-button"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            type="button"
            onClick={() => setIsExpanded(true)}
            className="group flex items-center gap-2.5 px-3 py-2 rounded-full bg-slate-900/95 hover:bg-slate-900 border border-amber-500/60 text-white shadow-xl hover:shadow-amber-500/20 backdrop-blur-md transition-all cursor-pointer"
            title={`Proxy Mode Active: Viewing as ${activeUser.name} (${activeUser.role}). Click to expand or exit.`}
          >
            <span className="flex h-2.5 w-2.5 relative">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-amber-500"></span>
            </span>
            <Eye className="w-4 h-4 text-amber-400 group-hover:scale-110 transition-transform" />
            <span className="font-bold text-[11px] text-amber-300 pr-1">
              Proxy: <span className="text-white font-medium">{activeUser.name?.split(' ')[0] || activeUser.role}</span>
            </span>
            <ChevronUp className="w-3.5 h-3.5 text-slate-400 group-hover:text-white transition-colors" />
          </motion.button>
        )}
      </AnimatePresence>
    </div>
  );
};
